

import { motion } from 'framer-motion';
import { ArrowRight } from 'lucide-react';

export default function Hero() {
  return (
    <section className="relative pt-32 pb-20 lg:pt-48 lg:pb-32 overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="text-center max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <h1 className="text-4xl sm:text-5xl lg:text-7xl font-bold tracking-tight text-primary mb-6">
              Everyone gets what they need to do their best work
            </h1>
            <p className="text-xl sm:text-2xl text-text-muted mb-10 max-w-2xl mx-auto leading-relaxed">
              Ianos helps teams work together without the friction. Gently block incomplete work before it reaches your team—with clear guidance on what's needed.
            </p>

            <div className="flex flex-col sm:flex-row justify-center items-center gap-4 mb-16">
              <a
                href="https://app.ianos.io"
                target="_blank"
                rel="noopener noreferrer"
                className="w-full sm:w-auto px-8 py-4 bg-accent hover:bg-accent-dark text-white rounded-lg font-semibold text-lg transition-all transform hover:scale-105 shadow-lg shadow-blue-500/20 flex items-center justify-center gap-2"
              >
                Request a Demo
                <ArrowRight size={20} />
              </a>
              <a
                href="#how-it-works"
                className="w-full sm:w-auto px-8 py-4 bg-white text-text-main border border-gray-200 hover:border-gray-300 hover:bg-gray-50 rounded-lg font-semibold text-lg transition-all flex items-center justify-center gap-2"
              >
                See How It Works
              </a>
            </div>
          </motion.div>

          {/* Abstract Dashboard Visual */}
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.2 }}
            className="relative mx-auto max-w-5xl"
          >
            <div className="rounded-xl bg-white shadow-2xl border border-gray-100 p-2 sm:p-4 overflow-hidden">
              <div className="rounded-lg bg-gray-50 aspect-[16/9] relative overflow-hidden flex items-center justify-center border border-gray-100">
                {/* Simulated UI Content */}
                <div className="absolute inset-0 bg-white">
                  {/* Header */}
                  <div className="h-12 border-b border-gray-100 flex items-center px-4 gap-2">
                    <div className="w-3 h-3 rounded-full bg-red-400"></div>
                    <div className="w-3 h-3 rounded-full bg-yellow-400"></div>
                    <div className="w-3 h-3 rounded-full bg-green-400"></div>
                  </div>
                  {/* Body */}
                  <div className="p-8 flex flex-col items-center justify-center h-full">
                    <div className="bg-white rounded-lg shadow-xl border border-blue-100 max-w-md w-full p-6 transform -translate-y-4">
                      <div className="flex items-start gap-4">
                        <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0 text-accent">
                          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"></path><path d="m9 12 2 2 4-4"></path></svg>
                        </div>
                        <div>
                          <h3 className="text-lg font-semibold text-primary mb-2">Almost ready!</h3>
                          <p className="text-text-muted mb-4">To help the team, this needs:</p>
                          <ul className="space-y-2 mb-4">
                            <li className="flex items-center gap-2 text-sm text-text-main bg-red-50 px-2 py-1 rounded border border-red-100">
                              <span className="w-4 h-4 rounded-full border border-red-400 flex items-center justify-center text-[10px] text-red-500">!</span>
                              Reproduction steps (min 25 chars)
                            </li>
                            <li className="flex items-center gap-2 text-sm text-text-main bg-red-50 px-2 py-1 rounded border border-red-100">
                              <span className="w-4 h-4 rounded-full border border-red-400 flex items-center justify-center text-[10px] text-red-500">!</span>
                              Severity level (P0/P1/P2/P3)
                            </li>
                          </ul>
                          <div className="text-xs text-text-light">Ianos helps you complete this so devs can start immediately.</div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Background Decor */}
            <div className="absolute -top-10 -right-10 w-64 h-64 bg-accent/5 rounded-full blur-3xl -z-10"></div>
            <div className="absolute -bottom-10 -left-10 w-64 h-64 bg-blue-400/5 rounded-full blur-3xl -z-10"></div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
